import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import VideoBgAdvertising from "../../assets/bgAdvertising.mp4";
import MediaLogo from "../../assets/MEDIA2-cropped.png";
import NavbarV3 from "../../components/Navbar/NavbarV3";
import AdvertisingRequestModal from "../../components/ContactUsButton/AdvertisingRequestModal";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import OutOfHomeHighlights from "../../components/OutOfHomeHighlights/OutOfHomeHighlights";
import OutOfHomeWhy from "../../components/OutOfHomeWhy/OutOfHomeWhy";
import OutOfHomeProcess from "../../components/OutOfHomeProcess/OutOfHomeProcess";
import OutOfHomeServiceLinks from "../../components/OutOfHomeServiceLinks/OutOfHomeServiceLinks";
import OutOfHomeFAQ from "../../components/OutOfHomeFAQ/OutOfHomeFAQ";
import OutOfHomeFinalCta from "../../components/OutOfHomeFinalCta/OutOfHomeFinalCta";
import Footer from "../../components/Footer/Footer";
import { getLangUrls } from "../../utils/languageRouting";
import "./OutOfHome.css";
import "./OutOfHomeSections.css";

// City-specific slugs, keyed the same way as the i18n
// outOfHomePage.cities / seo.outOfHomeCities entries. Adding a new city
// only requires a new key here + a matching i18n entry + a new <Route>
// in App.js (and scripts/prerender.js's ROUTES) - the page itself is
// shared, not duplicated per city.
const CITY_SLUGS = {
  brossard: "affichage-numerique-brossard",
  chateauguay: "affichage-numerique-chateauguay",
  valleyfield: "affichage-numerique-valleyfield",
  beauharnois: "affichage-numerique-beauharnois",
};

const OutOfHome = ({ cityKey }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const citySlug = cityKey ? CITY_SLUGS[cityKey] : null;
  const seoTitle = cityKey
    ? t(`seo.outOfHomeCities.${cityKey}.title`)
    : t("seo.outOfHome.title");
  const seoDescription = cityKey
    ? t(`seo.outOfHomeCities.${cityKey}.description`)
    : t("seo.outOfHome.description");
  // City pages are French-only for now (no /en/affichage-numerique-...
  // equivalent exists yet), so they keep a fixed FR canonical and no
  // hreflang alternates - there's nothing to point one at. The base
  // /out-of-home page has a real EN twin, so it uses the shared FR/EN
  // helper like the other core pages.
  const seo = citySlug
    ? {
        lang: "fr",
        canonical: `https://www.distributiondrinq.ca/${citySlug}`,
      }
    : getLangUrls("/out-of-home", location.pathname);
  const cityH1 = cityKey ? t(`outOfHomePage.cities.${cityKey}.h1`) : null;
  const cityParagraph = cityKey
    ? t(`outOfHomePage.cities.${cityKey}.localParagraph`)
    : null;
  const cityFacts = cityKey
    ? {
        population: t(`outOfHomePage.cities.${cityKey}.facts.population`),
        distance: t(`outOfHomePage.cities.${cityKey}.facts.distance`),
        region: t(`outOfHomePage.cities.${cityKey}.facts.region`),
      }
    : null;

  // ⬅️ Add modal state
  const [showModal, setShowModal] = useState(false);
  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const handleScrollToLocations = () => {
    const homePath = seo.lang === "en" ? "/en" : "/";
    if (location.pathname !== homePath) {
      navigate(homePath, { replace: false });
      setTimeout(() => {
        document
          .getElementById("locations")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document
        .getElementById("locations")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="d-flex flex-column">
      <Helmet>
        <html lang={seo.lang} />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seo.canonical} />
        {seo.hreflangFr && (
          <link rel="alternate" hrefLang="fr" href={seo.hreflangFr} />
        )}
        {seo.hreflangEn && (
          <link rel="alternate" hrefLang="en" href={seo.hreflangEn} />
        )}
        {seo.hreflangFr && (
          <link rel="alternate" hrefLang="x-default" href={seo.hreflangFr} />
        )}
      </Helmet>

      <NavbarV3
        logo={MediaLogo}
        logoAlt="DRINQ Média"
        logoHeightTop="105px"
        logoHeightScrolled="72px"
        logoHeightMobile="39px"
      />

      <div
        className="position-relative w-100"
        style={{ maxHeight: "750px", overflow: "hidden" }}
      >
        <video
          src={VideoBgAdvertising}
          autoPlay
          loop
          muted
          playsInline
          className="w-100"
          style={{ objectFit: "cover", height: "750px" }}
        />

        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ background: "rgba(0, 0, 0, 0.0)" }}
        />

        <div
          className="position-absolute top-50 text-white w-100"
          style={{ transform: "translateY(-50%)" }}
        >
          <div className="text-md-start text-center ps-md-5 ps-lg-4 ps-xl-5">
            {/* Desktop H1 */}
            {cityH1 ? (
              <h1
                className="fw-bold mb-4 d-none d-xl-block"
                style={{ fontSize: "95px", letterSpacing: "0.01em", lineHeight: 1.05 }}
              >
                {cityH1}
              </h1>
            ) : (
              <h1
                className="fw-bold mb-4 d-none d-xl-block"
                style={{ fontSize: "95px", letterSpacing: "0.01em", lineHeight: 1.05 }}
                dangerouslySetInnerHTML={{ __html: t("outOfHomePage.hero.title") }}
              />
            )}

            {/* Mobile / Tablet H1 */}
            {cityH1 ? (
              <h1
                className="fw-bold mb-4 d-block d-xl-none"
                style={{ fontSize: "45px", letterSpacing: "0.01em", lineHeight: 1.05 }}
              >
                {cityH1}
              </h1>
            ) : (
              <h1
                className="fw-bold mb-4 d-block d-xl-none"
                style={{ fontSize: "45px", letterSpacing: "0.01em", lineHeight: 1.05 }}
                dangerouslySetInnerHTML={{ __html: t("outOfHomePage.hero.title") }}
              />
            )}

            {/* City-specific facts strip, local paragraph, and discreet
                back link, inserted under the H1 and before the rest of
                the usual (shared) hero content, as requested. */}
            {cityKey && (
              <>
                <div
                  className="d-flex flex-wrap align-items-center mb-3"
                  style={{
                    gap: "10px",
                    fontSize: "13px",
                    letterSpacing: "0.03em",
                    textTransform: "uppercase",
                    color: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <span>
                    {t("outOfHomePage.cities.factsLabels.population")}
                    {": "}
                    <strong style={{ color: "#fff" }}>{cityFacts.population}</strong>
                  </span>
                  <span aria-hidden="true">•</span>
                  <span>
                    {t("outOfHomePage.cities.factsLabels.distance")}
                    {": "}
                    <strong style={{ color: "#fff" }}>{cityFacts.distance}</strong>
                  </span>
                  <span aria-hidden="true">•</span>
                  <span>
                    {t("outOfHomePage.cities.factsLabels.region")}
                    {": "}
                    <strong style={{ color: "#fff" }}>{cityFacts.region}</strong>
                  </span>
                </div>
                <p
                  className="fw-light mb-2"
                  style={{ fontSize: "18px", maxWidth: "640px" }}
                >
                  {cityParagraph}
                </p>
                <Link
                  to="/out-of-home"
                  className="d-inline-block mb-4"
                  style={{
                    fontSize: "14px",
                    color: "rgba(255, 255, 255, 0.75)",
                    textDecoration: "underline",
                  }}
                >
                  {t("outOfHomePage.cities.backLink")}
                </Link>
              </>
            )}

            {/* Desktop subtitle */}
            <p
              className="fw-light mb-4 d-none d-xl-block"
              style={{ fontSize: "22px", maxWidth: "640px" }}
            >
              {t("outOfHomePage.hero.subtitle")}
            </p>

            {/* Mobile / Tablet subtitle */}
            <p
              className="fw-light mb-4 d-block d-xl-none mx-auto mx-md-0"
              style={{ fontSize: "16px", maxWidth: "480px" }}
            >
              {t("outOfHomePage.hero.subtitle")}
            </p>

            <div className="d-flex flex-wrap justify-content-center justify-content-md-start gap-3">
              <Button
                style={{
                  backgroundColor: "white",
                  borderColor: "white",
                }}
                onClick={openModal}
                className="button-advertising start-advertising-btn px-2 py-2 px-xl-4 py-xl-3 rounded-4 fw-semibold d-flex align-items-center gap-2"
              >
                <span className="ooh-gradient-text">
                  {t("outOfHomePage.hero.button")}
                </span>
              </Button>

              <Button
                variant="outline-light"
                onClick={handleScrollToLocations}
                className="button-advertising start-advertising-btn px-2 py-2 px-xl-4 py-xl-3 rounded-4 fw-semibold d-flex align-items-center gap-2"
              >
                {t("outOfHomePage.hero.secondaryButton")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="oohs-backdrop">
        <OutOfHomeHighlights />
        <OutOfHomeWhy openModal={openModal} />
        <OutOfHomeProcess />
        {!citySlug && <OutOfHomeServiceLinks />}
        <OutOfHomeFAQ />
        <OutOfHomeFinalCta openModal={openModal} />
      </div>
      <Footer />

      {/* ⬅️ Add the modal at the bottom */}
      <AdvertisingRequestModal show={showModal} handleClose={closeModal} />
    </div>
  );
};

export default OutOfHome;
